import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Tag,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import SportSelect from 'components/SportSelect';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormSection from '../../../components/form-section';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import CourtAvailabilityTypePicker from '../../court-management/components/CourtAvailabilityTypePicker';
import SubCourtPicker from '../../court-management/components/SubCourtPicker';
import { useCreateCourt } from '../../court-management/hooks/useCreateCourt';
import { getDefaultCurrency } from '../../court-pricing-management/components/CurrencyPicker';
import LocationPicker from '../../location-management/components/LocationPicker';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSingleClubAutoSelect } from '../../location-management/hooks/useSingleClubAutoSelect';
import { blockRangeMinutes, parseBlockPrice } from '../model';
import { usePricingGridState, makeInitialGridState } from '../usePricingGridState';
import { buildGridCompactBlocks, buildGridTemplateRules, countGridCells } from '../utils/build-grid-compact-blocks';
import CourtPricingGridEditor from './CourtPricingGridEditor';

type CourtFields = {
    name: string;
    sportTypes: SportType[];
    location?: OrganizationLocationDto;
    isComposite: boolean;
    subCourtIds: number[];
    isOutdoor: boolean;
    coachingPriority?: number;
};

const LEGACY_DEFAULT_HOURLY_PRICE = 10;
const HOUR_MS = 60 * 60 * 1000;

function todayAtMidnight(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

function emptyFields(location?: OrganizationLocationDto): CourtFields {
    return { name: '', sportTypes: [], location, isComposite: false, subCourtIds: [], isOutdoor: false };
}

/**
 * The grid-based "add court" modal, shipped NEXT TO `CreateCourtForm` so the
 * two can be compared on real schedules before either replaces the other.
 * Identity, structure and availability-type sections are the same as the
 * existing form; the schedule section is the pricing grid. It sends the same
 * `POST courts` payload (compact blocks + template), so nothing changes
 * server-side.
 *
 * Deliberately NOT carried over from `CreateCourtForm`: the optional
 * "create an event with this court" section — it is unrelated to pricing
 * and events can be created from their own page.
 */
export default function CreateCourtGridForm() {
    const navigation = useNavigate();
    const toast = useToast();
    const modal = useDisclosure();
    const { selectedLocation } = useSelectedLocation();

    const [fields, setFields] = useState<CourtFields>(() => emptyFields(selectedLocation));
    const [isGameAvailable, setIsGameAvailable] = useState(true);
    const [isCoachingAvailable, setIsCoachingAvailable] = useState(false);
    const [returnAsOneChunk, setReturnAsOneChunk] = useState(false);
    const [startDate, setStartDate] = useState(todayAtMidnight);
    const [endDate, setEndDate] = useState(() => {
        const date = new Date(Date.now() + 364 * 24 * HOUR_MS);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [currency] = useState<Currency>(() => getDefaultCurrency());
    const [saveError, setSaveError] = useState<string | null>(null);
    const grid = usePricingGridState();

    useEffect(() => {
        setFields((f) => ({ ...f, location: selectedLocation }));
    }, [selectedLocation]);

    const setField = <K extends keyof CourtFields>(key: K, value: CourtFields[K]) => setFields((f) => ({ ...f, [key]: value }));

    const { isSingleClub } = useSingleClubAutoSelect({
        selectedLocation: fields.location,
        onSelect: (loc) => setField('location', loc),
    });

    const { mutateAsync: createCourt, isLoading: isCreating } = useCreateCourt((created) => {
        navigation(`/admin/courtDetails?court=${created.id}`);
    });

    const close = () => {
        modal.onClose();
        setFields(emptyFields(fields.location));
        grid.setState(makeInitialGridState());
        setIsGameAvailable(true);
        setIsCoachingAvailable(false);
        setReturnAsOneChunk(false);
        setSaveError(null);
    };

    const counts = countGridCells(grid.state, fields.sportTypes);
    const hasInvalidBlock = grid.state.blocks.some((b) => {
        const range = blockRangeMinutes(b);
        return range === null || range[0] >= range[1];
    });
    const canSubmit =
        fields.name.trim().length > 0 &&
        fields.sportTypes.length > 0 &&
        Boolean(fields.location) &&
        (isGameAvailable || isCoachingAvailable) &&
        !hasInvalidBlock &&
        endDate >= startDate &&
        counts.priced > 0;

    const handleSubmit = async () => {
        if (!canSubmit || !fields.location) {
            if (counts.priced === 0) {
                toast({ title: translate('courts.pricing_grid.nothing_priced'), status: 'error', duration: 5000, isClosable: true });
            }
            return;
        }
        setSaveError(null);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const blocks = buildGridCompactBlocks({ state: grid.state, sports: fields.sportTypes, currency, returnAsOneChunk });
        const firstFlatPrice = grid.state.blocks.map((b) => parseBlockPrice(b.priceRaw)).find((p): p is number => p !== null);
        try {
            await createCourt({
                locationId: fields.location.id,
                sportTypes: fields.sportTypes,
                name: fields.name.trim(),
                isComposite: fields.isComposite,
                subCourtIds: fields.subCourtIds,
                isOutdoor: fields.isOutdoor,
                coachingPriority: isCoachingAvailable ? fields.coachingPriority : undefined,
                // Legacy columns no booking path reads; kept populated like the
                // existing form does so the two forms produce comparable rows.
                defaultHourlyPrice: firstFlatPrice ?? LEGACY_DEFAULT_HOURLY_PRICE,
                defaultHourlyPriceCurrency: currency,
                defaultHourlyPriceDuration: HOUR_MS,
                availabilityTemplate: {
                    id: v4(),
                    courtId: Number(fields.location.id),
                    startDate,
                    endDate,
                    appliedToAllTime: true,
                    isGameAvailable,
                    isCoachingAvailable,
                    returnAsOneChunk,
                    rules: buildGridTemplateRules(grid.state, fields.sportTypes, currency, v4),
                },
                compactAvailabilities: {
                    timeZone,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    returnAsOneChunk,
                    blocks,
                },
            });
            toast({ title: translate('courts.court_created'), status: 'success', duration: 3000, isClosable: true });
            close();
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || translate('error');
            setSaveError(message);
            toast({
                title: translate('courts.create_court_error'),
                description: message,
                status: 'error',
                duration: 10000,
                isClosable: true,
                position: 'top',
            });
        }
    };

    return (
        <>
            <Button variant="lightBrand" onClick={modal.onOpen} rightIcon={<Tag size="sm" colorScheme="accent">{translate('courts.pricing_grid.beta_badge')}</Tag>}>
                {translate('courts.pricing_grid.add_court_with_grid')}
            </Button>
            <FormModal
                isOpen={modal.isOpen}
                onClose={close}
                title={translate('courts.pricing_grid.add_court_with_grid')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isCreating}
                isSubmitDisabled={!canSubmit}
                size="5xl"
            >
                <FormStack density="section">
                    {saveError && (
                        <Alert status="error" borderRadius="md" data-testid="pricing-grid-save-error">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                <strong>{translate('courts.pricing_grid.save_refused')}</strong> — {saveError}
                            </AlertDescription>
                        </Alert>
                    )}
                    <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">{translate('courts.pricing_grid.beta_hint')}</AlertDescription>
                    </Alert>

                    <FormSection label={translate('courts.title_in_form')}>
                        <FormField
                            type="text"
                            label={translate('courts.title_in_form')}
                            value={fields.name}
                            onChange={(value) => setField('name', value)}
                            isRequired
                        />
                        <FormControl isInvalid={fields.sportTypes.length === 0}>
                            <FormLabel>{translate('courts.sports')}</FormLabel>
                            <SportSelect value={fields.sportTypes} onChange={(next) => setField('sportTypes', next)} />
                            <FormErrorMessage>{translate('courts.pricing_grid.no_sport')}</FormErrorMessage>
                        </FormControl>
                        {!isSingleClub && (
                            <FormControl isInvalid={!fields.location}>
                                <FormLabel>{translate('courts.club')}</FormLabel>
                                <LocationPicker selectedLocation={fields.location} setSelectedLocation={(loc) => setField('location', loc)} />
                                <FormErrorMessage>{translate('courts.must_select_club')}</FormErrorMessage>
                            </FormControl>
                        )}
                    </FormSection>

                    <FormSection label={translate('courts.is_composite')}>
                        <FormControl>
                            <Checkbox
                                isChecked={fields.isComposite}
                                onChange={(e) => setFields((f) => ({ ...f, isComposite: e.target.checked, subCourtIds: e.target.checked ? f.subCourtIds : [] }))}
                                colorScheme="brand"
                                size="lg"
                            >
                                {translate('courts.is_composite')}
                            </Checkbox>
                            <FormHelperText>{translate('courts.is_composite_helper')}</FormHelperText>
                        </FormControl>
                        {fields.isComposite && (
                            <SubCourtPicker selectedSubCourtIds={fields.subCourtIds} setSelectedSubCourtIds={(ids) => setField('subCourtIds', ids)} />
                        )}
                        <FormControl>
                            <Checkbox isChecked={fields.isOutdoor} onChange={(e) => setField('isOutdoor', e.target.checked)} colorScheme="brand" size="lg">
                                {translate('courts.is_outdoor')}
                            </Checkbox>
                            <FormHelperText>{translate('courts.is_outdoor_description')}</FormHelperText>
                        </FormControl>
                    </FormSection>

                    <FormSection label={translate('courts.court_availability_type_picker.title')}>
                        <CourtAvailabilityTypePicker
                            isGameAvailable={isGameAvailable}
                            setIsGameAvailable={setIsGameAvailable}
                            isCoachingAvailable={isCoachingAvailable}
                            setIsCoachingAvailable={setIsCoachingAvailable}
                        />
                        {isCoachingAvailable && (
                            <FormField
                                type="number"
                                label={translate('courts.coaching_priority')}
                                helperText={translate('courts.coaching_priority_helper')}
                                value={fields.coachingPriority ?? ''}
                                min={1}
                                step={1}
                                placeholder={translate('courts.coaching_priority_placeholder')}
                                onChange={(value) => setField('coachingPriority', value)}
                                onEmpty={() => setField('coachingPriority', undefined)}
                            />
                        )}
                    </FormSection>

                    <FormSection label={translate('courts.availabilities')}>
                        <CourtPricingGridEditor
                            grid={grid}
                            sports={fields.sportTypes}
                            currency={currency}
                            returnAsOneChunk={returnAsOneChunk}
                            onReturnAsOneChunkChange={setReturnAsOneChunk}
                            startDate={startDate}
                            endDate={endDate}
                            onStartDateChange={setStartDate}
                            onEndDateChange={setEndDate}
                        />
                    </FormSection>
                </FormStack>
            </FormModal>
        </>
    );
}
